import { FixedStepper, type BodyHandle, type ShapeHandle, type Vec3, type World } from 'box3d-web';

export type SportsBallId = 'football' | 'basketball' | 'tennis';
export type BallSurfaceId = 'grass' | 'hardwood' | 'hard-court';

export interface CurvePoint {
  x: number;
  y: number;
}

export type AerodynamicEvidenceLevel =
  | 'measured-ball-specific'
  | 'research-shaped-proxy'
  | 'sphere-proxy';

export interface AerodynamicCalibrationEvidence {
  sourceUrl: string;
  evidence: AerodynamicEvidenceLevel;
  note: string;
}

export interface SportsBallSpec {
  id: SportsBallId;
  label: string;
  radiusM: number;
  massKg: number;
  radiusRangeM: readonly [number, number];
  massRangeKg: readonly [number, number];
  dropTest: {
    heightM: number;
    reboundRangeM: readonly [number, number];
  };
  material: {
    friction: number;
    rollingResistance: number;
    restitutionByImpactSpeed: readonly CurvePoint[];
  };
  body: {
    ccd: boolean;
    /** Local diagonal inertia as `factor * mass * radius^2`; 2/3 models a thin shell. */
    inertiaFactor: number;
    linearDamping: number;
    angularDamping: number;
    gravityScale: number;
  };
  aero: {
    airDensityKgM3: number;
    dynamicViscosityPaS: number;
    dragByReynolds: readonly CurvePoint[];
    liftBySpinRatio: readonly CurvePoint[];
    calibration: {
      drag: AerodynamicCalibrationEvidence;
      lift: AerodynamicCalibrationEvidence;
    };
  };
  visual: {
    color: string;
    accent: string;
  };
}

export interface BallSurfaceSpec {
  id: BallSurfaceId;
  label: string;
  friction: number;
  restitution: number;
  rollingResistance: number;
  color: string;
}

export type BallSurfaceOverrides = Partial<Omit<BallSurfaceSpec, 'id' | 'label'>> & {
  label?: string;
};

export interface SportsBallOverrides {
  radiusM?: number;
  massKg?: number;
  material?: Partial<SportsBallSpec['material']>;
  body?: Partial<SportsBallSpec['body']>;
  /** Calibration provenance belongs to the reference preset and cannot be overridden by a game profile. */
  aero?: Partial<Omit<SportsBallSpec['aero'], 'calibration'>>;
}

export interface SportsBallRuntime {
  spec: SportsBallSpec;
  surface: BallSurfaceSpec;
  body: BodyHandle;
  shape: ShapeHandle;
  densityKgM3: number;
  lastRestitution: number;
}

export interface BallSurfaceInteraction {
  effectiveFriction: number;
  effectiveRollingResistance: number;
  effectiveRestitutionAtImpactSpeed: number;
}

export interface BallForceSample {
  relativeVelocity: Vec3;
  reynolds: number;
  spinRatio: number;
  dragCoefficient: number;
  liftCoefficient: number;
  dragForce: Vec3;
  magnusForce: Vec3;
  totalForce: Vec3;
}

export interface BallStepOptions {
  /** Retained for callers that already sample transforms for telemetry. */
  position?: Vec3;
  wind?: Vec3;
  /** Unit or non-unit normal of the surface the ball is approaching. */
  impactNormal?: Vec3;
}

export type BallStepOptionsProvider =
  | BallStepOptions
  | ((ball: SportsBallRuntime) => BallStepOptions);

export interface SportsBallSimulationOptions {
  /** Canonical collision/broad-phase tick. Defaults to 240 Hz. */
  fixedDt?: number;
  /** Constraint-solver substeps inside each collision tick. Defaults to 2. */
  solverSubsteps?: number;
  maxDeltaClamp?: number;
  maxStepsPerAdvance?: number;
}

export const SPORTS_BALL_PHYSICS_HZ = 240;
export const SPORTS_BALL_FIXED_DT = 1 / SPORTS_BALL_PHYSICS_HZ;

/**
 * World-level sports-ball runner.
 *
 * Box3D refreshes broad-phase pairs and contacts once per `world.step` call;
 * its `substeps` argument only refines constraint solving. This runner therefore
 * issues one real world step per canonical physics quantum and uses an
 * accumulator solely to decouple render/game cadence from collision cadence.
 * All balls in the world are advanced together so their interactions remain
 * physically coherent.
 */
export class SportsBallSimulation {
  readonly fixedDt: number;
  readonly solverSubsteps: number;
  private readonly world: World;
  private readonly balls = new Set<SportsBallRuntime>();
  private readonly stepper: FixedStepper;
  private readonly lastForces = new Map<SportsBallRuntime, BallForceSample>();

  constructor(
    world: World,
    balls: Iterable<SportsBallRuntime> = [],
    options: SportsBallSimulationOptions = {},
  ) {
    this.world = world;
    this.fixedDt = options.fixedDt ?? SPORTS_BALL_FIXED_DT;
    this.solverSubsteps = Math.max(1, Math.floor(options.solverSubsteps ?? 2));
    this.stepper = new FixedStepper({
      fixedDt: this.fixedDt,
      substeps: this.solverSubsteps,
      maxDeltaClamp: options.maxDeltaClamp ?? 0.1,
      maxStepsPerFrame: options.maxStepsPerAdvance ?? 24,
    });
    for (const ball of balls) this.addBall(ball);
  }

  addBall(ball: SportsBallRuntime): void {
    this.balls.add(ball);
  }

  removeBall(ball: SportsBallRuntime): void {
    this.balls.delete(ball);
    this.lastForces.delete(ball);
  }

  advance(
    delta: number,
    stepOptions: BallStepOptionsProvider = {},
    onAfterStep?: (dt: number) => void,
  ): number {
    return this.stepper.advance(delta, (dt) => {
      for (const ball of this.balls) {
        const options = typeof stepOptions === 'function' ? stepOptions(ball) : stepOptions;
        this.lastForces.set(ball, stepSportsBall(this.world, ball, options));
      }
      this.world.step(dt, this.solverSubsteps);
      onAfterStep?.(dt);
    });
  }

  getLastForce(ball: SportsBallRuntime): BallForceSample | undefined {
    return this.lastForces.get(ball);
  }

  get simTime(): number {
    return this.stepper.simTime;
  }

  resetClock(): void {
    this.stepper.reset();
  }
}

const STANDARD_AIR_DENSITY = 1.204;
const STANDARD_AIR_VISCOSITY = 1.825e-5;

// Regulation geometry/mass and rebound windows come from the 2026 IFAB/FIFA,
// 2024 FIBA, and 2026 ITF tests. Aerodynamic curves are intentionally exposed
// calibration baselines, not claimed governing-body constants.
export const SPORTS_BALL_PRESETS: Readonly<Record<SportsBallId, SportsBallSpec>> = {
  football: {
    id: 'football',
    label: 'Football',
    radiusM: 0.1098,
    massKg: 0.43,
    radiusRangeM: [0.1082, 0.1114],
    massRangeKg: [0.41, 0.45],
    dropTest: { heightM: 2, reboundRangeM: [1.35, 1.55] },
    material: {
      friction: 0.62,
      rollingResistance: 0.004,
      restitutionByImpactSpeed: [
        { x: 0, y: 0.9 },
        { x: 6.3, y: 0.895 },
        { x: 20, y: 0.86 },
      ],
    },
    body: { ccd: true, inertiaFactor: 2 / 3, linearDamping: 0, angularDamping: 0.035, gravityScale: 1 },
    aero: {
      airDensityKgM3: STANDARD_AIR_DENSITY,
      dynamicViscosityPaS: STANDARD_AIR_VISCOSITY,
      dragByReynolds: [
        { x: 0, y: 0.47 },
        { x: 1.8e5, y: 0.47 },
        { x: 2.4e5, y: 0.15 },
        { x: 4e5, y: 0.18 },
        { x: 8e5, y: 0.18 },
      ],
      liftBySpinRatio: [
        { x: 0, y: 0 },
        { x: 0.1, y: 0.08 },
        { x: 0.3, y: 0.2 },
        { x: 0.6, y: 0.3 },
      ],
      calibration: {
        drag: {
          sourceUrl: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC3657093/',
          evidence: 'measured-ball-specific',
          note: 'Wind-tunnel drag-crisis anchors for modern soccer balls; curve is a compact interpolation of reported regimes.',
        },
        lift: {
          sourceUrl: 'https://www.sciencedirect.com/science/article/pii/S1877705812016414',
          evidence: 'research-shaped-proxy',
          note: 'Shape follows ball-flight research across spin parameter and Reynolds ranges; it is not an exact digitized fit.',
        },
      },
    },
    visual: { color: '#f7f3e8', accent: '#20242a' },
  },
  basketball: {
    id: 'basketball',
    label: 'Basketball',
    radiusM: 0.1209,
    massKg: 0.6,
    radiusRangeM: [0.1194, 0.1225],
    massRangeKg: [0.58, 0.62],
    dropTest: { heightM: 1.8, reboundRangeM: [1.035, 1.085] },
    material: {
      friction: 0.82,
      rollingResistance: 0.006,
      restitutionByImpactSpeed: [
        { x: 0, y: 0.81 },
        { x: 5.9, y: 0.8 },
        { x: 18, y: 0.76 },
      ],
    },
    body: { ccd: true, inertiaFactor: 2 / 3, linearDamping: 0, angularDamping: 0.055, gravityScale: 1 },
    aero: {
      airDensityKgM3: STANDARD_AIR_DENSITY,
      dynamicViscosityPaS: STANDARD_AIR_VISCOSITY,
      dragByReynolds: [
        { x: 0, y: 0.54 },
        { x: 5e5, y: 0.54 },
      ],
      liftBySpinRatio: [
        { x: 0, y: 0 },
        { x: 0.1, y: 0.07 },
        { x: 0.3, y: 0.18 },
        { x: 0.6, y: 0.27 },
      ],
      calibration: {
        drag: {
          sourceUrl: 'https://doi.org/10.1016/j.proeng.2010.04.145',
          evidence: 'measured-ball-specific',
          note: 'Constant drag coefficient estimated from 500 Hz free-flight measurements of an NBA basketball.',
        },
        lift: {
          sourceUrl: 'https://doi.org/10.1017/aer.2019.15',
          evidence: 'sphere-proxy',
          note: 'Smooth spinning-sphere Magnus response is used only as a tunable baseline, not basketball-specific calibration.',
        },
      },
    },
    visual: { color: '#d96c20', accent: '#1d1510' },
  },
  tennis: {
    id: 'tennis',
    label: 'Tennis',
    radiusM: 0.0335,
    massKg: 0.0577,
    radiusRangeM: [0.0327, 0.0343],
    massRangeKg: [0.056, 0.0594],
    dropTest: { heightM: 2.54, reboundRangeM: [1.35, 1.47] },
    material: {
      friction: 0.76,
      rollingResistance: 0.007,
      restitutionByImpactSpeed: [
        { x: 0, y: 0.79 },
        { x: 7.1, y: 0.78 },
        { x: 30, y: 0.74 },
      ],
    },
    body: { ccd: true, inertiaFactor: 2 / 3, linearDamping: 0, angularDamping: 0.075, gravityScale: 1 },
    aero: {
      airDensityKgM3: STANDARD_AIR_DENSITY,
      dynamicViscosityPaS: STANDARD_AIR_VISCOSITY,
      dragByReynolds: [
        { x: 0, y: 0.507 },
        { x: 2e5, y: 0.507 },
      ],
      liftBySpinRatio: [
        { x: 0, y: 0 },
        { x: 0.1, y: 0.09 },
        { x: 0.3, y: 0.22 },
        { x: 0.6, y: 0.34 },
      ],
      calibration: {
        drag: {
          sourceUrl: 'https://doi.org/10.1007/s12283-013-0144-9',
          evidence: 'measured-ball-specific',
          note: 'Uses the reported mean flight drag coefficient across measured speed and spin ranges.',
        },
        lift: {
          sourceUrl: 'https://doi.org/10.1007/s12283-013-0144-9',
          evidence: 'research-shaped-proxy',
          note: 'Curve follows the measured spin-parameter relationship but is not a shot-level regression fit.',
        },
      },
    },
    visual: { color: '#d8f33d', accent: '#f5f8df' },
  },
};

export const BALL_SURFACES: Readonly<Record<BallSurfaceId, BallSurfaceSpec>> = {
  grass: {
    id: 'grass',
    label: 'Grass',
    friction: 0.7,
    restitution: 0.05,
    rollingResistance: 0.035,
    color: '#2f7f47',
  },
  hardwood: {
    id: 'hardwood',
    label: 'Hardwood',
    friction: 0.78,
    restitution: 0.08,
    rollingResistance: 0.009,
    color: '#b77a3c',
  },
  'hard-court': {
    id: 'hard-court',
    label: 'Hard court',
    friction: 0.62,
    restitution: 0.05,
    rollingResistance: 0.014,
    color: '#356fa3',
  },
};

export function resolveBallSurfaceSpec(
  id: BallSurfaceId,
  overrides: BallSurfaceOverrides = {},
): BallSurfaceSpec {
  const surface = { ...BALL_SURFACES[id], ...overrides, id };
  validateBallSurfaceSpec(surface);
  return surface;
}

export function validateBallSurfaceSpec(surface: BallSurfaceSpec): void {
  if (!surface.label.trim()) throw new RangeError('Surface label must not be empty.');
  for (const [name, value] of [
    ['friction', surface.friction],
    ['restitution', surface.restitution],
    ['rollingResistance', surface.rollingResistance],
  ] as const) {
    if (!Number.isFinite(value) || value < 0) {
      throw new RangeError(`Surface ${name} must be finite and non-negative.`);
    }
  }
  if (surface.restitution > 1) throw new RangeError('Surface restitution must not exceed 1.');
}

/** Mirrors Box3D's default material mixing rules for transparent calibration. */
export function resolveBallSurfaceInteraction(
  ball: SportsBallSpec,
  surface: BallSurfaceSpec,
  impactSpeedMps: number,
): BallSurfaceInteraction {
  validateSportsBallSpec(ball);
  validateBallSurfaceSpec(surface);
  const ballRestitution = interpolateCurve(
    ball.material.restitutionByImpactSpeed,
    Math.max(0, impactSpeedMps),
  );
  return {
    effectiveFriction: Math.sqrt(ball.material.friction * surface.friction),
    effectiveRollingResistance: Math.max(
      ball.material.rollingResistance,
      surface.rollingResistance,
    ),
    effectiveRestitutionAtImpactSpeed: Math.max(ballRestitution, surface.restitution),
  };
}

export function resolveSportsBallSpec(id: SportsBallId, overrides: SportsBallOverrides = {}): SportsBallSpec {
  const base = SPORTS_BALL_PRESETS[id];
  const resolved: SportsBallSpec = {
    ...base,
    radiusM: overrides.radiusM ?? base.radiusM,
    massKg: overrides.massKg ?? base.massKg,
    material: {
      ...base.material,
      ...overrides.material,
      restitutionByImpactSpeed:
        overrides.material?.restitutionByImpactSpeed ?? base.material.restitutionByImpactSpeed.map((point) => ({ ...point })),
    },
    body: { ...base.body, ...overrides.body },
    aero: {
      ...base.aero,
      ...overrides.aero,
      dragByReynolds: overrides.aero?.dragByReynolds ?? base.aero.dragByReynolds.map((point) => ({ ...point })),
      liftBySpinRatio: overrides.aero?.liftBySpinRatio ?? base.aero.liftBySpinRatio.map((point) => ({ ...point })),
      calibration: {
        drag: { ...base.aero.calibration.drag },
        lift: { ...base.aero.calibration.lift },
      },
    },
    visual: { ...base.visual },
  };
  validateSportsBallSpec(resolved);
  return resolved;
}

export function validateSportsBallSpec(spec: SportsBallSpec): void {
  if (!(spec.radiusM > 0 && Number.isFinite(spec.radiusM))) throw new RangeError('Ball radius must be positive.');
  if (!(spec.massKg > 0 && Number.isFinite(spec.massKg))) throw new RangeError('Ball mass must be positive.');
  if (typeof spec.body.ccd !== 'boolean') throw new RangeError('Ball ccd must be boolean.');
  for (const [name, value] of [
    ['material.friction', spec.material.friction],
    ['material.rollingResistance', spec.material.rollingResistance],
    ['body.linearDamping', spec.body.linearDamping],
    ['body.angularDamping', spec.body.angularDamping],
    ['body.gravityScale', spec.body.gravityScale],
  ] as const) {
    if (!Number.isFinite(value) || value < 0) {
      throw new RangeError(`Ball ${name} must be finite and non-negative.`);
    }
  }
  if (!Number.isFinite(spec.body.inertiaFactor) || spec.body.inertiaFactor <= 0) {
    throw new RangeError('Ball body.inertiaFactor must be finite and positive.');
  }
  for (const [name, value] of [
    ['aero.airDensityKgM3', spec.aero.airDensityKgM3],
    ['aero.dynamicViscosityPaS', spec.aero.dynamicViscosityPaS],
  ] as const) {
    if (!Number.isFinite(value) || value <= 0) {
      throw new RangeError(`Ball ${name} must be finite and positive.`);
    }
  }
  validateCurve('restitutionByImpactSpeed', spec.material.restitutionByImpactSpeed, 0, 1, 0);
  validateCurve('dragByReynolds', spec.aero.dragByReynolds, 0, Number.POSITIVE_INFINITY, 0);
  validateCurve('liftBySpinRatio', spec.aero.liftBySpinRatio, 0, Number.POSITIVE_INFINITY, 0);
  for (const [name, evidence] of Object.entries(spec.aero.calibration)) {
    if (!/^https:\/\//.test(evidence.sourceUrl) || !evidence.note.trim()) {
      throw new RangeError(`Ball aero.calibration.${name} needs an HTTPS source and explanatory note.`);
    }
  }
}

export function sphereDensityKgM3(massKg: number, radiusM: number): number {
  return massKg / ((4 / 3) * Math.PI * radiusM ** 3);
}

export function createSportsBall(
  world: World,
  spec: SportsBallSpec,
  surface: BallSurfaceSpec,
  position: Vec3,
): SportsBallRuntime {
  validateSportsBallSpec(spec);
  const densityKgM3 = sphereDensityKgM3(spec.massKg, spec.radiusM);
  const body = world.createBody({
    type: 'dynamic',
    position,
    ccd: spec.body.ccd,
    linearDamping: spec.body.linearDamping,
    angularDamping: spec.body.angularDamping,
    gravityScale: spec.body.gravityScale,
  });
  const initialRestitution = interpolateCurve(spec.material.restitutionByImpactSpeed, 0);
  const shape = world.addSphere(body, spec.radiusM, {
    density: densityKgM3,
    friction: spec.material.friction,
    restitution: initialRestitution,
    rollingResistance: Math.max(spec.material.rollingResistance, surface.rollingResistance),
  });
  const inertia = spec.body.inertiaFactor * spec.massKg * spec.radiusM ** 2;
  world.setBodyInertia(body, [inertia, inertia, inertia]);
  return { spec, surface, body, shape, densityKgM3, lastRestitution: initialRestitution };
}

export function stepSportsBall(
  world: World,
  runtime: SportsBallRuntime,
  options: BallStepOptions,
): BallForceSample {
  const velocity = world.getLinearVelocity(runtime.body);
  const angularVelocity = world.getAngularVelocity(runtime.body);
  const wind = options.wind ?? [0, 0, 0];
  const forces = computeBallForces(runtime.spec, [velocity.x, velocity.y, velocity.z], [angularVelocity.x, angularVelocity.y, angularVelocity.z], wind);

  if (lengthSquared(forces.totalForce) > 0) {
    world.applyForce(runtime.body, forces.totalForce);
  }

  // Shape restitution must be ready before Box3D enters contact. A proximity
  // band is timestep-dependent: at a coarse outer step the body can cross the
  // whole band and collide before this update runs. Instead, continuously set
  // the predicted value from approach speed along the relevant surface normal.
  // The default is a horizontal playing surface; wall/ramp scenarios can pass
  // their own normal without changing the ball preset.
  const impactNormal = options.impactNormal ?? [0, 1, 0];
  const normalLength = Math.sqrt(lengthSquared(impactNormal));
  const approachSpeed = normalLength > 1e-9
    ? Math.max(
        0,
        -(
          velocity.x * impactNormal[0] +
          velocity.y * impactNormal[1] +
          velocity.z * impactNormal[2]
        ) / normalLength,
      )
    : 0;
  if (approachSpeed > 0) {
    const restitution = interpolateCurve(runtime.spec.material.restitutionByImpactSpeed, approachSpeed);
    if (Math.abs(restitution - runtime.lastRestitution) > 1e-4) {
      world.setShapeRestitution(runtime.shape, restitution);
      runtime.lastRestitution = restitution;
    }
  }

  return forces;
}

export function launchSportsBall(world: World, runtime: SportsBallRuntime, velocity: Vec3, angularVelocity: Vec3 = [0, 0, 0]): void {
  world.setLinearVelocity(runtime.body, velocity);
  world.setAngularVelocity(runtime.body, angularVelocity);
  world.wakeBody(runtime.body);
}

export function computeBallForces(spec: SportsBallSpec, velocity: Vec3, angularVelocity: Vec3, wind: Vec3 = [0, 0, 0]): BallForceSample {
  const relativeVelocity: Vec3 = [velocity[0] - wind[0], velocity[1] - wind[1], velocity[2] - wind[2]];
  const speed = Math.sqrt(lengthSquared(relativeVelocity));
  const spin = Math.sqrt(lengthSquared(angularVelocity));
  const diameter = spec.radiusM * 2;
  const reynolds = speed > 0 ? (spec.aero.airDensityKgM3 * speed * diameter) / spec.aero.dynamicViscosityPaS : 0;
  const spinRatio = speed > 1e-6 ? (spec.radiusM * spin) / speed : 0;
  const dragCoefficient = interpolateCurve(spec.aero.dragByReynolds, reynolds);
  const liftCoefficient = interpolateCurve(spec.aero.liftBySpinRatio, spinRatio);
  const area = Math.PI * spec.radiusM ** 2;
  const dynamicPressureArea = 0.5 * spec.aero.airDensityKgM3 * speed ** 2 * area;

  const dragForce: Vec3 = speed > 1e-6
    ? scale(relativeVelocity, (-dynamicPressureArea * dragCoefficient) / speed)
    : [0, 0, 0];

  const magnusAxis = cross(angularVelocity, relativeVelocity);
  const magnusAxisLength = Math.sqrt(lengthSquared(magnusAxis));
  const magnusForce: Vec3 = magnusAxisLength > 1e-6
    ? scale(magnusAxis, (dynamicPressureArea * liftCoefficient) / magnusAxisLength)
    : [0, 0, 0];

  return {
    relativeVelocity,
    reynolds,
    spinRatio,
    dragCoefficient,
    liftCoefficient,
    dragForce,
    magnusForce,
    totalForce: add(dragForce, magnusForce),
  };
}

export function interpolateCurve(points: readonly CurvePoint[], x: number): number {
  if (points.length === 0) throw new RangeError('A calibration curve needs at least one point.');
  if (x <= points[0].x) return points[0].y;
  for (let i = 1; i < points.length; i += 1) {
    const right = points[i];
    if (x <= right.x) {
      const left = points[i - 1];
      const span = right.x - left.x;
      const t = span > 0 ? (x - left.x) / span : 0;
      return left.y + (right.y - left.y) * t;
    }
  }
  return points[points.length - 1].y;
}

function validateCurve(
  name: string,
  points: readonly CurvePoint[],
  minY: number,
  maxY = Number.POSITIVE_INFINITY,
  minX = Number.NEGATIVE_INFINITY,
): void {
  if (points.length === 0) throw new RangeError(`${name} needs at least one point.`);
  let previousX = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    if (!Number.isFinite(point.x) || point.x < minX || point.x < previousX) {
      throw new RangeError(`${name} x values must be finite, sorted, and at least ${minX}.`);
    }
    if (!Number.isFinite(point.y) || point.y < minY || point.y > maxY) throw new RangeError(`${name} contains an invalid y value.`);
    previousX = point.x;
  }
}

function lengthSquared(value: Vec3): number {
  return value[0] ** 2 + value[1] ** 2 + value[2] ** 2;
}

function scale(value: Vec3, amount: number): Vec3 {
  return [value[0] * amount, value[1] * amount, value[2] * amount];
}

function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
